import ExcelJS from "exceljs";
import raDataDownloaderWorker from "../../workers/raDataDownloaderWorker";
import WebWorker from "../../workers/WebWorker";
import { formatSuggestedSettings, saveExcelFile } from "../../utils/Utils";
import {
  createProcessedDataExcelSheet,
  processCleanDataForExcel,
} from "./processedDataSheet";
import { cleanedData } from "../../network/cipAPI";
import { store } from "../../network/networkUtil";

const createCleaningAdvisorExcelSheet = (
  workbook,
  sheetData,
  columns,
  sheetName,
  t,
  transposeData = false,
  suggestedData = null,
) => {
  const excelSheet = workbook.addWorksheet(sheetName);
  if (sheetData) {
    if (transposeData) {
      if (columns.length === sheetData.length) {
        for (let i = 0; i < columns.length; i++) {
          const suggestedValue =
            suggestedData && suggestedData[i] != null ? suggestedData[i] : "";
          const row = excelSheet.addRow([
            columns[i],
            sheetData[i],
            suggestedValue,
          ]);
          row.getCell(1).font = { bold: true };
          row.getCell(2).alignment = { horizontal: "right" };
          if (suggestedData) {
            row.getCell(3).alignment = { horizontal: "right" };
          }
        }
        excelSheet.getColumn(1).width = 50;
        if (suggestedData) {
          excelSheet.getColumn(2).width = 20;
          excelSheet.getColumn(3).width = 20;
        }
      }
    } else {
      excelSheet.getRow(1).font = { bold: true };
      excelSheet.columns = Object.keys(columns).map((key) => ({
        key,
        header: columns[key],
        width: 20,
      }));
      excelSheet.addRows(sheetData);
    }
  }
  if (sheetName === t("excelSheetNames.others")) {
    excelSheet.insertRow(36);
    const dividerRow = excelSheet.insertRow(37);
    excelSheet.insertRow(38);
    dividerRow.height = 30;
    dividerRow.getCell("A").value = t("analyseData.settings");
    dividerRow.getCell("A").font = { bold: true, size: 12 };
    if (suggestedData) {
      dividerRow.getCell("B").value =
        t("excelSheetNames.othersColumnCurrentValue") || "Current";
      dividerRow.getCell("B").font = { bold: true, size: 12 };
      dividerRow.getCell("C").value =
        t("excelSheetNames.othersColumnSuggestedValue") || "Suggested";
      dividerRow.getCell("C").font = { bold: true, size: 12 };
    }
    dividerRow.border = {
      top: { style: "medium", color: { argb: "FFD3D3D3" } },
      bottom: { style: "medium", color: { argb: "FFD3D3D3" } },
    };
  }
};

const transposeData = (data) => {
  const transposedData = Object.keys(data[0])
    .map((key) => data.map((row) => row[key]))
    .flat();

  return transposedData;
};
const transposeColumns = (columns, otherSheetData) => {
  const permeate_quality = otherSheetData[0]["permeate"];
  const pressure = otherSheetData[0]["pressure"];
  const flow = otherSheetData[0]["flow"];
  return Object.keys(columns).map((key) => {
    let column = columns[key];
    if (columns[key].includes("Flow")) {
      column = column.replace("<unit>", flow);
    } else if (
      columns[key].includes("设计的产水流量") ||
      columns[key].includes("最小产水流量")
    ) {
      column = column.replace("<unit>", flow);
    } else if (columns[key].includes("最差产水水质")) {
      column = column.replace("<unit>", permeate_quality);
    } else if (columns[key].includes("Permeate")) {
      column = column.replace("<unit>", permeate_quality);
    } else if (
      columns[key].includes("最大进水压力") ||
      columns[key].includes("初始压差") ||
      columns[key].includes("最大压差")
    ) {
      column = column.replace("<unit>", pressure);
    } else if (columns[key].includes("Pressure")) {
      column = column.replace("<unit>", pressure);
    }
    return column;
  });
};

const addReplacementAnalysisCipScheduleNote = (
  workbook,
  sheetName,
  recommendedCipsFlag,
  t,
) => {
  const excelSheet = workbook.getWorksheet(sheetName);
  if (!excelSheet) return;

  const scheduleType = t(
    `replacementAnalysisStrings.${
      recommendedCipsFlag === 1 ? "recommended" : "predicted"
    }`,
  );

  excelSheet.addRows([
    [],
    [],
    [t("replacementAnalysisStrings.cipScheduleNote", { scheduleType })],
  ]);
};

export const exportAllReplacementTables = async (
  downloadFileName,
  exportData,
  translatedRowData,
  t,
  selectedTrain,
) => {
  // If cleanData is not present, fetch it from CA API (cleanedData endpoint only exists on /ca)
  let finalExportData = { ...exportData };

  if (!finalExportData.cleanData && selectedTrain) {
    const currentApp = store.getState().app;
    try {
      // Temporarily switch to CA to call cleanedData API
      store.dispatch({ type: "STORE_APP", payload: "ca" });
      const response = await cleanedData(selectedTrain);
      if (response?.data?.cleaned_data) {
        const data = JSON.parse(response.data.cleaned_data);
        finalExportData.cleanData = data;
        finalExportData.units = response.data.units;
      }
    } catch (err) {
      console.error("Error fetching cleanedData for export:", err);
      // Continue with export even if cleanData fetch fails
    } finally {
      // Restore original app state
      store.dispatch({ type: "STORE_APP", payload: currentApp });
    }
  }

  // Process cleanData before sending to worker
  if (finalExportData.cleanData) {
    const processedResult = processCleanDataForExcel(
      finalExportData.cleanData,
      finalExportData.units,
    );
    if (processedResult) {
      finalExportData.processedDataSheetData = processedResult.data;
      finalExportData.processedDataUnits = processedResult.units;
    }
    delete finalExportData.cleanData; // Remove raw cleanData as it's no longer needed
  }

  // Build suggestedOthers from Redux store
  const suggestedSettingsRaw = store.getState().suggestedSettings;
  const suggestedOthers = formatSuggestedSettings(suggestedSettingsRaw, t);
  finalExportData.suggestedOthers = suggestedOthers;

  const worker = new WebWorker(raDataDownloaderWorker);

  const translations = {
    exportWorkerTransaltionString: t("exportWorkerTransaltionString", {
      returnObjects: true,
    }),
  };
  worker.postMessage({
    exportData: finalExportData,
    translations,
    translatedRowData,
  });
  worker.onerror = (error) => console.log("Error in Worker:", error);
  worker.onmessage = (event) => {
    const {
      replacementAnalysisSheetData,
      otherSheetData,
      replacementPredictionSheetData,
      processedDataSheetData,
      suggestedOtherSheetData,
    } = event.data;

    const workbook = new ExcelJS.Workbook();

    const ReplacementAnalysisStringsObj = t("ReplacementAnalysisStrings", {
      returnObjects: true,
    });
    createCleaningAdvisorExcelSheet(
      workbook,
      replacementAnalysisSheetData,
      ReplacementAnalysisStringsObj,
      t("excelSheetNames.replacementAnalysis"),
      t,
    );
    addReplacementAnalysisCipScheduleNote(
      workbook,
      t("excelSheetNames.replacementAnalysis"),
      finalExportData.recommendedCipsFlag,
      t,
    );
    const ReplacementPredictionStringsObj = t("ReplacementPredictionStrings", {
      returnObjects: true,
    });
    createCleaningAdvisorExcelSheet(
      workbook,
      replacementPredictionSheetData,
      ReplacementPredictionStringsObj,
      t("excelSheetNames.prediction"),
      t,
    );

    const OthersTableStringsObj = t("RAOthersTableStrings", {
      returnObjects: true,
    });
    const transposedOtherSheetData = transposeData(otherSheetData);
    const transposedOtherSheetColumn = transposeColumns(
      OthersTableStringsObj,
      otherSheetData,
    );

    let transposedSuggestedData = null;
    if (suggestedOtherSheetData && suggestedOtherSheetData.length > 0) {
      transposedSuggestedData = Object.keys(otherSheetData[0]).map(
        (key) => suggestedOtherSheetData[0][key] ?? "",
      );
    }

    createCleaningAdvisorExcelSheet(
      workbook,
      transposedOtherSheetData,
      transposedOtherSheetColumn,
      t("excelSheetNames.others"),
      t,
      true,
      transposedSuggestedData,
    );

    // Add Processed Data sheet with cleaned data used for plotting
    const processedDataUnits = finalExportData.processedDataUnits;
    createProcessedDataExcelSheet(
      workbook,
      processedDataSheetData,
      t("excelSheetNames.processedData") || "Processed Data",
      t,
      processedDataUnits,
    );

    saveExcelFile(downloadFileName, workbook);
    worker.terminate();
  };
};

export default exportAllReplacementTables;
